import { useEffect, useEffectEvent, useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SentinelAnalyticsSelection } from './use-sentinel-selection';
import type { SentinelChannel } from '@/types/sentinelAnalytics';
import { CHANNEL_LABEL, UNCATEGORIZED_CATEGORY } from './definitions';
import type { FilterOptions } from './dimension-filters';
import { FilterPill } from '@/components/ui/filter-pill';
import { navigateOptions } from '@/components/ui/navigate-options';

/** Picks apply this long after the last click, so a burst of clicks makes one request. */
const APPLY_DELAY_MS = 500;

type FilterPicks = Pick<SentinelAnalyticsSelection, 'channels' | 'categories' | 'subCategories'>;

interface AnalyticsFiltersProps {
  selection: FilterPicks;
  options: FilterOptions | undefined;
  disabled: boolean;
  /** A dimension tab already splits by this, so its filter is hidden. */
  hide?: 'category' | 'channel';
  onChange: (next: Partial<FilterPicks>) => void;
}

interface OptionListProps {
  label: string;
  allLabel: string;
  options: string[];
  selected: string[];
  /** Display text per option value; the value itself is shown when missing. */
  labels?: Record<string, string>;
  onChange: (next: string[]) => void;
}

/** Checkbox list with an "All" row; picking every option is the same as "All". */
function OptionList({ label, allLabel, options, selected, labels, onChange }: OptionListProps) {
  const allSelected = options.length > 0 && options.every((option) => selected.includes(option));
  const rows = [{ label: allLabel, value: null }, ...options.map((option) => ({ label: labels?.[option] ?? option, value: option }))];
  return (
    <div role='listbox' aria-label={label} aria-multiselectable='true' className='custom-scrollbar flex min-h-0 flex-1 snap-y snap-mandatory flex-col gap-1.5 overflow-y-auto'>
      {rows.map((row) => {
        const checked = row.value === null ? allSelected : selected.includes(row.value);
        return (
          <button
            key={row.value ?? 'all'}
            type='button'
            role='option'
            aria-label={`${row.label}, ${checked ? 'selected' : 'not selected'}`}
            aria-selected={checked}
            disabled={options.length === 0}
            onKeyDown={navigateOptions}
            onClick={() => onChange(row.value === null ? (allSelected ? [] : options) : checked ? selected.filter((value) => value !== row.value) : [...selected, row.value])}
            className={cn('group flex w-full shrink-0 snap-start items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] leading-[18px] text-text-emphasis outline-none transition-colors hover:bg-gray-100 active:bg-gray-200 focus-visible:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50', row.value === null && 'relative mb-[7px] after:absolute after:inset-x-2.5 after:-bottom-[7px] after:h-px after:bg-gray-500')}
          >
            {/* Figma's Check-Box states: hover and press darken the box, focus adds an indigo-300 ring. */}
            <span aria-hidden='true' className='flex size-4 shrink-0 items-center justify-center'>
              <span
                className={cn(
                  'flex size-3.5 items-center justify-center rounded-[4px] border group-focus-visible:outline-2 group-focus-visible:outline-offset-1 group-focus-visible:outline-indigo-300',
                  checked
                    ? 'border-indigo-500 bg-indigo-500 text-white group-hover:bg-indigo-600 group-active:bg-indigo-700'
                    : 'border-gray-500 bg-white group-hover:bg-gray-100 group-active:bg-gray-300',
                )}
              >
                {checked ? <Check className='size-3' strokeWidth={2.5} /> : null}
              </span>
            </span>
            <span className='min-w-0 flex-1 break-words'>{row.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The Channel and Category menus, as kim-ai-frontend's Sentinel Analytics draws them. */
export function AnalyticsFilters({ selection, options, disabled, hide, onChange }: AnalyticsFiltersProps) {
  // Picks stay a draft until clicking pauses or the menu closes.
  const [draft, setDraft] = useState<Partial<FilterPicks>>({});
  const shown = { ...selection, ...draft };
  // A pick the current range or brand has no tickets for stays listed, so it can still be seen and cleared.
  const channels = [...new Set([...(options?.channels ?? []), ...shown.channels])];
  const categories = [...new Set([...(options?.categories ?? []), UNCATEGORIZED_CATEGORY, ...shown.categories])];
  const subCategories = [...new Set([...(options?.subCategories ?? []), ...shown.subCategories])];
  const subCategoriesOf = (picked: string[]): string[] => picked.flatMap((category) => options?.subCategoriesByCategory[category] ?? []);
  const everyCategoryPicked = (picked: string[]): boolean => categories.every((category) => picked.includes(category));
  // The badge counts what was picked, not the sub-categories that came with a category.
  const coveredSubCategories = everyCategoryPicked(shown.categories) ? subCategories : subCategoriesOf(shown.categories);
  const categoryCount = shown.categories.length + shown.subCategories.filter((sub) => !coveredSubCategories.includes(sub)).length;
  // Sub-categories of the picked categories come first, so they can be seen without scrolling.
  const subCategoryOptions = [...subCategories].sort((a, b) => Number(coveredSubCategories.includes(b)) - Number(coveredSubCategories.includes(a)));
  const applyDraft = () => {
    if (Object.keys(draft).length > 0) onChange(draft);
    setDraft({});
  };
  // Picking a category picks its sub-categories; unpicking it clears them. Every category picked
  // picks every sub-category, including any the data cannot pair with a category.
  const pickCategories = (next: string[]) => {
    const added = subCategoriesOf(next.filter((category) => !shown.categories.includes(category)));
    // A sub-category shared with a category that stays picked stays picked too.
    const kept = subCategoriesOf(next);
    const removed = subCategoriesOf(shown.categories.filter((category) => !next.includes(category))).filter((sub) => !kept.includes(sub));
    const subs = everyCategoryPicked(next)
      ? subCategories
      : next.length === 0
        ? []
        : [...new Set([...shown.subCategories.filter((sub) => !removed.includes(sub)), ...added])];
    setDraft((previous) => ({ ...previous, categories: next, subCategories: subs }));
  };
  // A category stays picked while any of its sub-categories is, and is picked again once all of them are.
  const pickSubCategories = (next: string[]) => {
    const allPicked = (category: string): boolean => {
      const subs = subCategoriesOf([category]);
      return subs.length > 0 && subs.every((sub) => next.includes(sub));
    };
    const kept = shown.categories.filter((category) => {
      const subs = subCategoriesOf([category]);
      return subs.length === 0 || subs.some((sub) => next.includes(sub));
    });
    const repicked = (options?.categories ?? []).filter((category) => !kept.includes(category) && allPicked(category));
    setDraft((previous) => ({ ...previous, categories: [...kept, ...repicked], subCategories: next }));
  };
  const applyPicks = useEffectEvent(applyDraft);
  useEffect(() => {
    if (Object.keys(draft).length === 0) return;
    const timer = setTimeout(applyPicks, APPLY_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applyPicks is an effect event, not a dependency
  }, [draft]);
  const closeApplies = (open: boolean) => !open && applyDraft();
  return (
    <>
      {hide !== 'channel' ? (
        <FilterPill label='Channel' count={shown.channels.length} disabled={disabled} contentClassName='w-[220px]' onOpenChange={closeApplies}>
          <OptionList label='Channel options' allLabel='All Channels' options={channels} labels={CHANNEL_LABEL} selected={shown.channels} onChange={(next) => setDraft((previous) => ({ ...previous, channels: next as SentinelChannel[] }))} />
        </FilterPill>
      ) : null}
      {hide !== 'category' ? (
        <FilterPill label='Category' count={categoryCount} disabled={disabled} contentClassName='grid w-[458px] grid-cols-2 gap-1.5 p-1.5' onOpenChange={closeApplies}>
          {/* Figma's overlay: two 220px columns, 361px lists of 30px rows 6px apart, so a list ends on a whole row. */}
          <div className='flex flex-col gap-2.5 pt-1.5 pr-3 pl-1.5'>
            <p className='text-xs font-medium leading-4 text-gray-800'>Categories</p>
            <div className='flex h-[361px] flex-col'>
              <OptionList label='Category options' allLabel='All Categories' options={categories} selected={shown.categories} onChange={pickCategories} />
            </div>
          </div>
          <div className='flex flex-col gap-2.5 pt-1.5'>
            <p className='text-xs font-medium leading-4 text-gray-800'>Sub Categories</p>
            <div className='flex h-[361px] flex-col rounded-[12px] border border-gray-500 bg-white px-1 py-3.5'>
              <OptionList label='Sub-category options' allLabel='All Sub categories' options={subCategoryOptions} selected={shown.subCategories} onChange={pickSubCategories} />
            </div>
          </div>
        </FilterPill>
      ) : null}
    </>
  );
}
